import { ComponentProps, useCallback, useEffect, useState, type FunctionComponent } from 'react';
import { Address, ADDRESS_LAYOUT_TYPES, showErrorToast } from '@fff-web/fff-ui-shared';
import { type Subscription } from '@fff-web/growth-sdk';
import { SubscriptionStatus } from '../MyAccountPage';
import { getAddresses } from '../services/api/address';
import PageContainer from '../shared-components/PageContainer/PageContainer';
import Credits from './Credits/Credits';
import ManageMembership from './ManageMembership/ManageMembership';
import Payment from './Payment/Payment';
import PersonalInformation from './PersonalInformation';
import SectionContainer from './SectionContainer/SectionContainer';
import * as styles from './AccountDetailsPage.module.css';

type AccountDetailsPageProps = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  shippingAddress: Record<string, unknown>;
  credits: {
    amount: number;
    creditName: string;
  }[];
  billingInfo: Record<string, unknown>;
  setBillingInfo: (billingInfo: Record<string, unknown>) => void;
  isClubMember: boolean;
  clubMemberSubscriptionId?: string | null;
  subscriptionState: SubscriptionStatus;
  refetchClubMemberSubscription: () => void;
  boxSubscriptions?: Subscription[] | null;
};

const AccountDetailsPage: FunctionComponent<AccountDetailsPageProps> = ({
  firstName,
  lastName,
  phone,
  email,
  shippingAddress,
  credits,
  billingInfo,
  setBillingInfo,
  isClubMember,
  clubMemberSubscriptionId,
  subscriptionState,
  refetchClubMemberSubscription,
  boxSubscriptions,
}) => {
  const { address1, address2, city, region, code: zipcode, country } = shippingAddress || {};

  const formState = {
    firstName,
    lastName,
    phone,
    email,
    address1,
    address2,
    city,
    region,
    zipcode,
    country,
  };
  const [formData, setFormData] = useState(formState);
  const [addresses, setAddresses] = useState<ComponentProps<typeof Address>['addressesData']>([]);

  const updateUserInfo = (data: Record<string, unknown>) => {
    setFormData((prevState) => {
      return { ...prevState, ...data };
    });
  };

  const fetchAddresses = useCallback(async () => {
    try {
      const addressResponse = await getAddresses();
      setAddresses(addressResponse);
    } catch (error) {
      showErrorToast(error as string);
    }
  }, []);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const showMembershipSectionForClubMember = !!clubMemberSubscriptionId;
  const showMembershipSectionForBoxMember = boxSubscriptions && boxSubscriptions.length > 0;

  return (
    <PageContainer>
      <div className={styles.container} data-testid="account-details-container">
        <div className={styles.header} data-testid="account-details-header">
          <h1>Account Details</h1>
        </div>
        <SectionContainer className={styles.sectionContainerTitle}>
          <PersonalInformation
            firstName={formData.firstName}
            lastName={formData.lastName}
            email={formData.email}
            updateUserInfo={updateUserInfo}
          />
        </SectionContainer>
        <SectionContainer title="Shipping Address" className={styles.sectionContainer}>
          <Address
            addressesData={addresses}
            layoutType={ADDRESS_LAYOUT_TYPES.userAccount}
            onNewAddressCreated={() => fetchAddresses()}
          />
        </SectionContainer>
        <SectionContainer className={styles.sectionContainerTitle}>
          <Payment billingInfo={billingInfo} setBillingInfo={setBillingInfo} />
        </SectionContainer>
        <SectionContainer title="Credits & Discounts" className={styles.sectionContainerTitle}>
          <Credits credits={credits} />
        </SectionContainer>
        {(!isClubMember ||
          showMembershipSectionForClubMember ||
          showMembershipSectionForBoxMember) && (
          <SectionContainer title="Manage Membership" className={styles.sectionContainer}>
            <ManageMembership
              isClubMember={isClubMember}
              clubMemberSubscriptionId={clubMemberSubscriptionId}
              subscriptionState={subscriptionState}
              refetchClubMemberSubscription={refetchClubMemberSubscription}
              boxSubscriptions={boxSubscriptions}
            />
          </SectionContainer>
        )}
      </div>
    </PageContainer>
  );
};
export default AccountDetailsPage;
